import React from 'react';
import { Trans, t } from '@lingui/macro';
import styled from 'styled-components';
import { Group, Text, Box, color, spacing } from '@landr/maestro';
import { Interval, CurrencyDto } from 'types/generated/subscriptionsApi';
import { addCurrencyToPrice } from 'utils/price';
import InfoPopover from 'components/InfoPopover/InfoPopover';
import { HandlePlanSelect } from '../../types';
import * as Styled from './styled';

export type PlanSelectorProps = {
    frequency: Interval;
    activePlanCode?: string;
    handlePlanSelect: HandlePlanSelect;
    creditsNeeded?: number;
};

const Usp: React.FC = ({ children }) => (
    <Styled.UspWrapper>
        <Text size="sm">{children}</Text>
    </Styled.UspWrapper>
);

const MonthlyPrice = ({
    monthlyEquivalentPrice,
    monthlyEquivalentSale,
}: {
    monthlyEquivalentPrice: string;
    monthlyEquivalentSale?: string;
}) => {
    const paidPrice = monthlyEquivalentSale || monthlyEquivalentPrice;

    return (
        <Box>
            <Trans>
                <Text size="lg" fontWeight="bold">
                    {paidPrice}
                </Text>
                <Text size="sm"> /mo</Text>
            </Trans>
        </Box>
    );
};

const Strike = styled.span`
    text-decoration: line-through;
    color: ${color('danger.500')};
    padding-right: ${spacing('xxs')};
`;

const BilledUpfront = ({
    subscriptionPrice,
    salePrice,
    isMonthly,
}: {
    subscriptionPrice: string;
    salePrice?: string;
    isMonthly: boolean;
}) => {
    if (isMonthly) {
        return <>&nbsp;</>;
    }
    // To preserve layout shift, render a placeholder component

    const paidPrice = salePrice || subscriptionPrice;

    return (
        <Text contrast="low" size="sm">
            {!!salePrice && <Strike>{subscriptionPrice}</Strike>}
            <Trans>{paidPrice} billed upfront</Trans>
        </Text>
    );
};

const MostPopular = ({ isMostPopular }: { isMostPopular: boolean }) => {
    if (!isMostPopular) {
        return null;
    }

    return (
        <Styled.MostPopularWrapper>
            <Text size="xs">
                <Trans>Most popular</Trans>
            </Text>
        </Styled.MostPopularWrapper>
    );
};

const PlanHeader = ({
    isChecked,
    title,
    info,
    monthlyEquivalentPrice,
    monthlyEquivalentSale,
}: {
    isChecked: boolean;
    title: string;
    info?: string;
    monthlyEquivalentPrice: string;
    monthlyEquivalentSale: string;
}) => (
    <Styled.HeaderGroup gutter="xs">
        <Styled.LeftColumn>
            <Styled.RadioNoPadding
                onChange={() => undefined}
                isChecked={isChecked}
                label=""
                size="md"
            />
        </Styled.LeftColumn>
        <Styled.PlanNameItem>
            <Text fontWeight="bold">{title}</Text>
            {info && (
                <InfoPopover
                    aria-label={t`Details Tooltip`}
                    isInverted={false}
                    isOutlined={false}
                    size="sm"
                >
                    {info}
                </InfoPopover>
            )}
        </Styled.PlanNameItem>
        <Styled.PlanCost>
            <MonthlyPrice
                monthlyEquivalentPrice={monthlyEquivalentPrice}
                monthlyEquivalentSale={monthlyEquivalentSale}
            />
        </Styled.PlanCost>
    </Styled.HeaderGroup>
);

const PlanContent = ({
    isMonthly,
    subscriptionPrice,
    salePrice,
    usp,
}: {
    isMonthly: boolean;
    subscriptionPrice: string;
    salePrice: string;
    usp: JSX.Element;
}) => (
    <Styled.ContentGroup gutter="xs">
        <Styled.LeftColumn></Styled.LeftColumn>
        <Styled.RightColumn>
            <Group direction="vertical" gutter="xs">
                <Group.Item>
                    <BilledUpfront
                        subscriptionPrice={subscriptionPrice}
                        salePrice={salePrice}
                        isMonthly={isMonthly}
                    />
                </Group.Item>
                <Group.Item>
                    <Usp>{usp}</Usp>
                </Group.Item>
            </Group>
        </Styled.RightColumn>
    </Styled.ContentGroup>
);

const Plan = ({
    activePlanCode,
    code,
    currency,
    frequency,
    isMostPopular = false,
    monthlyEquivalentPrice,
    subscriptionPrice,
    title,
    info,
    usp,
    handlePlanSelect,
    salePrice,
    monthlyEquivalentSale,
}: {
    activePlanCode?: string;
    code?: string;
    currency?: CurrencyDto;
    frequency: Interval;
    isMostPopular?: boolean;
    monthlyEquivalentPrice?: number;
    subscriptionPrice?: number;
    title: string;
    info?: string;
    usp: JSX.Element;
    handlePlanSelect: HandlePlanSelect;
    salePrice?: number;
    monthlyEquivalentSale?: number;
}) => {
    if (!code || !currency || !subscriptionPrice || !monthlyEquivalentPrice) {
        return null;
    }

    const isChecked = activePlanCode === code;
    const monthlyEquivalentPriceWithCurrency = addCurrencyToPrice(
        currency,
        monthlyEquivalentPrice,
    );
    const subscriptionPriceWithCurrency = addCurrencyToPrice(
        currency,
        subscriptionPrice,
    );

    const monthlyEquivalentSaleWithCurrency = addCurrencyToPrice(
        currency,
        monthlyEquivalentSale,
    );

    const salePriceWithCurrency = addCurrencyToPrice(currency, salePrice);

    return (
        <Group.Item>
            <Styled.PlanWrapper onClick={() => handlePlanSelect(code)}>
                <MostPopular isMostPopular={isMostPopular} />
                <Styled.InnerWrapper isChecked={isChecked}>
                    <PlanHeader
                        isChecked={isChecked}
                        title={title}
                        info={info}
                        monthlyEquivalentPrice={
                            monthlyEquivalentPriceWithCurrency
                        }
                        monthlyEquivalentSale={
                            monthlyEquivalentSaleWithCurrency
                        }
                    />
                    <PlanContent
                        isMonthly={frequency === 'Monthly'}
                        subscriptionPrice={subscriptionPriceWithCurrency}
                        salePrice={salePriceWithCurrency}
                        usp={usp}
                    />
                </Styled.InnerWrapper>
            </Styled.PlanWrapper>
        </Group.Item>
    );
};

export default Plan;
